/**
 * How a lineage graph's variable names line up with the questionnaire's and the data's fields.
 *
 * Lineage identifiers are wrapped and chained by the engine (`<<Reason for NPS - X>>`,
 * `<<A>>::<<B>>`); field labels are full "//" paths below the root (`Section//Q5`). For display the
 * wrappers go; for matching both sides are also case-folded with one path separator and single
 * spaces (the editor can leave NBSPs in labels), so the two line up.
 */

// The name without the engine's <<>> wrappers, and with a literal NBSP (U+00A0) as a space.
export const displayLabel = (s: string): string =>
  s.replace(/<<|>>/g, "").replace(/\u00A0/g, " ").trim()

// The name as matching compares it: "Parent//Option" and <<Parent>>::<<Option>> both become
// "parent::option".
export const normLabel = (s: string): string =>
  displayLabel(s)
    .replace(/\s*\/\/\s*/g, "::")
    .replace(/\s*::\s*/g, "::")
    .replace(/\s+/g, " ")
    .toLowerCase()

// A field label for display: "Parent//Option" as "Parent › Option".
export const displayFieldLabel = (s: string): string => displayLabel(s).replace(/\s*\/\/\s*/g, " › ")

/**
 * The entry for a lineage variable in an index keyed by `normLabel` of the full path. The full path
 * matches first. A name without its parents (a lineage leaf `<<Q5>>` for the field `Section//Q5`)
 * matches the path that ends with it, only when exactly one does.
 */
export const lookupByLabel = <T,>(index: Map<string, T>, leaf: string): T | undefined => {
  const norm = normLabel(leaf)
  if (!norm) return undefined
  if (index.has(norm)) return index.get(norm)
  let found: T | undefined
  let hits = 0
  index.forEach((value, key) => {
    if (key.endsWith(`::${norm}`)) {
      hits += 1
      found = value
    }
  })
  return hits === 1 ? found : undefined
}

/**
 * The fields of a multi-variable question (a multi-select or grid has one per answer or row,
 * "Parent//Option") when the lineage variable is the question itself. The question is found as
 * `lookupByLabel` finds a field: its full path, else the one path ending with its name. Flat data
 * names ("Parent - Option" columns) match the name as it is.
 */
export const lookupFamily = <T,>(index: Map<string, T>, leaf: string): T[] => {
  const norm = normLabel(leaf)
  if (!norm) return []
  // The question paths in the index that the name can stand for.
  const parents = new Set<string>()
  index.forEach((_, key) => {
    for (let at = key.indexOf("::"); at !== -1; at = key.indexOf("::", at + 2)) {
      const parent = key.slice(0, at)
      if (parent === norm || parent.endsWith(`::${norm}`)) parents.add(parent)
    }
  })
  const parent = parents.has(norm) ? norm : parents.size === 1 ? [...parents][0] : undefined
  const out: T[] = []
  index.forEach((value, key) => {
    if ((parent && key.startsWith(`${parent}::`)) || key.startsWith(`${norm} - `)) out.push(value)
  })
  return out
}

/**
 * Whether a lineage variable's name stands for more than one question, so neither lookup can
 * pick one: no field or question has it as its full path, and it ends more than one path.
 */
export const isAmbiguousLabel = <T,>(index: Map<string, T>, leaf: string): boolean => {
  const norm = normLabel(leaf)
  if (!norm || index.has(norm)) return false
  const paths = new Set<string>()
  let exactParent = false
  index.forEach((_, key) => {
    if (key.endsWith(`::${norm}`)) paths.add(key)
    for (let at = key.indexOf("::"); at !== -1; at = key.indexOf("::", at + 2)) {
      const parent = key.slice(0, at)
      if (parent === norm) exactParent = true
      else if (parent.endsWith(`::${norm}`)) paths.add(parent)
    }
  })
  return !exactParent && paths.size > 1
}
